import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC, MutableRefObject } from 'react';
import { useFormContext } from 'react-hook-form';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { iconSizes, InputSizes, InputType } from './Input.constants';
import { SField, SInputContainer, SLabel, SError, SIconContainer, SInfo } from './Input.styled';
import TooltipError from '../tooltipError/TooltipError';
import { dotSyntaxResolver, hasError } from '@/common/dotSyntaxResolver';
import TooltipWarning from '../tooltipWarning';

export interface IInputProps {
  name: string;
  label?: string;
  posteriorLabel?: string;
  validations?: any;
  size?: 's' | 'm';
  type?: 'primary' | 'secondary' | 'minimal';
  disabled?: boolean;
  icon?: string;
  tooltip?: string;
  info?: string;
  tooltipWarnMsg?: string;
  tooltipWarning?: boolean;
  tooltipError?: boolean;
  maxLength?: number;
  width?: number;
  className?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => boolean | void;
  extraOnChange?: (event: any) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  uppercase?: boolean;
  inputType?: string;
  inputRef?: React.ForwardedRef<HTMLInputElement>;
  isReadOnly?: boolean;
  allowOnlyNumbersWithoutDecimal?: boolean;
  allowOnlyNumbers?: boolean;
  allowOnlyCurrencyChars?: boolean;
  allowOnlySingleEntry?: boolean;
  autoFocus?: boolean;
}

const Input: FC<IInputProps> = ({
  name,
  label,
  posteriorLabel,
  validations,
  size = InputSizes.s,
  type = InputType.primary,
  disabled = false,
  icon = '',
  tooltip = '',
  width = 0,
  info = '',
  tooltipError = false,
  tooltipWarning = false,
  tooltipWarnMsg = '',
  maxLength = undefined,
  className = '',
  uppercase = false,
  onKeyDown,
  extraOnChange,
  onBlur,
  inputType = 'text',
  inputRef,
  isReadOnly = false,
  allowOnlyNumbersWithoutDecimal = false,
  allowOnlyNumbers = false,
  allowOnlyCurrencyChars = false,
  allowOnlySingleEntry = false,
  autoFocus = false,
}) => {
  const commonProps = { size, type, disabled };
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const { ref, ...rest } = register(name, validations);

  const onlyNumbersWithoutDecimal = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const val = value.replace(/[^0-9]/g, '');
    if (val != '0') {
      event.target.value = val;
    } else {
      event.target.value = '';
    }
  };

  const onlyNumbers = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const val = value.replace(/[^0-9.]/g, '');
    event.target.value = val;
  };

  const onlyCurrencyChars = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key.charCodeAt(0);
    const allowed = [46, 8, 65, 66, 68, 69, 72];
    for (let i = 48; i < 58; i++) allowed.push(i);

    if (allowed.indexOf(key) === -1) {
      event.preventDefault();
    }
  };

  const OnlySingleEntry = (event: React.ChangeEvent<HTMLInputElement>) => {
    const textValue = event.target.value;
    const textValues = textValue.split(',');
    for (let i = 0; i < textValues.length; i++) {
      //IN 718270 - Don't allow newlines in the values
      const val = textValues[i].replace('\r', '').replace('\n', '').trimStart().trimEnd(); // ITSM 740358

      if (val.length > 0) {
        // Ignore values with zero length - probably caused by consecutive commas entered in textbox
        event.target.value = val;
        break;
      } else {
        event.target.value = '';
      }
    }
  };

  return (
    <SField
      {...commonProps}
      width={width}
      className={`${className} ${isReadOnly ? 'read-only' : ''} ${inputType === 'hidden' ? 'hidden' : ''}`}
    >
      {type !== InputType.minimal && (
        <div style={{ display: 'flex' }}>
          <div style={{ display: 'flex' }}>
            {label && (
              <SLabel {...commonProps} className={className}>
                {label}
              </SLabel>
            )}
            {hasError(name, errors, disabled) && tooltipError && (
              <TooltipError message={dotSyntaxResolver(name, errors).message} />
            )}
            {tooltipWarning && tooltipWarnMsg.length > 0 && <TooltipWarning message={tooltipWarnMsg} />}
          </div>
        </div>
      )}
      <SInputContainer
        {...commonProps}
        hasError={hasError(name, errors, disabled)}
        hasWarning={tooltipWarning}
        withIcon={Boolean(icon.length)}
      >
        <input
          autoFocus={autoFocus}
          style={{ textTransform: uppercase ? 'uppercase' : 'unset' }}
          className={type}
          maxLength={maxLength}
          id={`txt${label?.replace(/ /g, '')}`}
          {...rest}
          disabled={disabled}
          {...validations}
          placeholder={type === InputType.minimal ? label : null}
          onKeyDown={(e) => {
            if (e.key === 'Tab' || (e.ctrlKey && (e.key === 'a' || e.key === 'c' || e.key === 'v' || e.key === 'x'))) {
              return;
            }
            allowOnlyCurrencyChars ? onlyCurrencyChars(e) : onKeyDown;
          }}
          onChange={(e) => {
            allowOnlyNumbersWithoutDecimal ? onlyNumbersWithoutDecimal(e) : onchange;
            allowOnlyNumbers ? onlyNumbers(e) : onchange;
            allowOnlySingleEntry ? OnlySingleEntry(e) : onchange;
            if (extraOnChange) extraOnChange(e);
          }}
          onBlurCapture={onBlur}
          type={inputType}
          ref={(e) => {
            ref(e);
            if (inputRef) {
              (inputRef as MutableRefObject<HTMLInputElement | null>).current = e; // you can still assign to ref
            }
          }}
        />
        <SInfo id={`info${label?.replace(/ /g, '')}`} className={`${isReadOnly ? 'read-only' : ''}`}>
          {info ?? ''}
        </SInfo>
        {icon && (
          <Tooltip anchorElement="target" position="top">
            <SIconContainer>
              <FontAwesomeIcon title={tooltip} icon={fal[icon]} style={{ fontSize: iconSizes[size] }} />
            </SIconContainer>
          </Tooltip>
        )}
      </SInputContainer>
      {posteriorLabel && <SLabel {...commonProps}>{posteriorLabel}</SLabel>}
      {hasError(name, errors, disabled) && !tooltipError && (
        <SError {...commonProps}>
          {errors[name] ? errors[name]?.message : dotSyntaxResolver(name, errors).message}
        </SError>
      )}
    </SField>
  );
};

export default Input;
